/**
 * Lesson Info Header - Displays key lesson information across the top
 */

import { useState } from 'react';
import { Box, Paper, Typography, Link, Chip, Divider, Select, MenuItem, SelectChangeEvent, CircularProgress, Button, Tooltip } from '@mui/material';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { LessonV1_SpineTagsPrediction, ReviewStatus } from '../../types';
import { getStatusConfig } from '../../utils/designSystem';
import { Article, School, Person, Flag, Save as SaveIcon, Cancel as CancelIcon, Feedback as FeedbackIcon, Visibility as ViewIcon, AutoAwesome as GenerateIcon } from '@mui/icons-material';
import { predictionsApi } from '../../services/predictions.service';
import { useAppStore } from '../../store/appStore';

interface LessonInfoHeaderProps {
  prediction: LessonV1_SpineTagsPrediction;
  // Action button props
  isDirty?: boolean;
  isSaving?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
  onFeedback?: () => void;
  // Status change props
  onStatusChange?: (status: ReviewStatus) => void;
  // Tag action props
  onViewTags?: () => void;
  onGenerateTags?: () => void;
  isGeneratingTags?: boolean;
}

export default function LessonInfoHeader({
  prediction,
  isDirty,
  isSaving,
  onSave,
  onCancel,
  onFeedback,
  onStatusChange,
  onViewTags,
  onGenerateTags,
  isGeneratingTags
}: LessonInfoHeaderProps) {
  const PDF_CDN_URL = import.meta.env.VITE_PDF_CDN_URL || 'https://d2ze058wumaaie.cloudfront.net';
  const pdfUrl = prediction.lesson_pdf ? `${PDF_CDN_URL}/${prediction.lesson_pdf}` : null;
  const { selectedProduct, selectedExperiment } = useAppStore();
  const queryClient = useQueryClient();
  const [localAssignee, setLocalAssignee] = useState<string>(prediction.assignee || '');
  const [localStatus, setLocalStatus] = useState<ReviewStatus>(prediction.review_status);
  const statusConfig = getStatusConfig(localStatus as any || 'PENDING');

  // Fetch available assignees
  const { data: filterValues } = useQuery({
    queryKey: ['filter-values', selectedProduct, selectedExperiment],
    queryFn: () => predictionsApi.getFilterValues(selectedProduct, selectedExperiment),
  });
  const availableAssignees = filterValues?.assignees || [];

  // Update assignee mutation
  const updateAssigneeMutation = useMutation({
    mutationFn: (newAssignee: string | null) =>
      predictionsApi.updateAssignee(selectedProduct, selectedExperiment, prediction.id, newAssignee),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prediction', selectedProduct, selectedExperiment, prediction.id] });
      queryClient.invalidateQueries({ queryKey: ['predictions'] });
    },
    onError: (error) => {
      console.error('Failed to update assignee:', error);
      setLocalAssignee(prediction.assignee || '');
    },
  });

  const handleAssigneeChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value;
    setLocalAssignee(newValue);
    const assigneeValue = newValue === '' ? null : newValue;
    updateAssigneeMutation.mutate(assigneeValue);
  };

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const newStatus = event.target.value as ReviewStatus;
    setLocalStatus(newStatus);
    if (onStatusChange) {
      onStatusChange(newStatus);
    }
  };

  return (
    <Paper sx={{ mb: 2, overflow: 'hidden' }}>
      {/* Main lesson info - prominent */}
      <Box sx={{ 
        p: 3, 
        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          {/* Left: Lesson title and details */}
          <Box sx={{ flex: 1, minWidth: 300 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5, color: 'primary.light' }}>
              {prediction.lesson_name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                <Box component="span" sx={{ fontWeight: 600 }}>Unit {prediction.unit_number}:</Box> {prediction.unit_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">•</Typography>
              <Typography variant="body2" color="text.secondary">
                Lesson {prediction.lesson_number}
              </Typography>
              <Typography variant="body2" color="text.secondary">•</Typography>
              <Chip 
                label={`Grade ${prediction.lesson_grade}`} 
                size="small" 
                sx={{ height: 22, fontWeight: 600 }}
              />
              <Chip 
                label={prediction.lesson_subject || 'N/A'} 
                size="small" 
                variant="outlined"
                sx={{ height: 22 }}
              />
            </Box>
          </Box>

          {/* Right: Status and main action buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {pdfUrl && (
              <Link
                href={pdfUrl}
                target="_blank"
                rel="noopener"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'primary.light',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                <Article fontSize="small" />
                View PDF
              </Link>
            )}

            {/* Status Dropdown */}
            <Select
              value={localStatus}
              onChange={handleStatusChange}
              size="small"
              disabled={!onStatusChange}
              sx={{
                minWidth: 140,
                height: 32,
                fontSize: '0.8125rem',
                fontWeight: 600,
                borderRadius: '16px',
                backgroundColor: statusConfig.backgroundColor,
                '& .MuiInputBase-root': {
                  borderRadius: '16px',
                  backgroundColor: statusConfig.backgroundColor,
                },
                '& .MuiSelect-select': {
                  py: 0.5,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: 'transparent',
                  color: statusConfig.color,
                  borderRadius: '16px',
                  '&:focus': {
                    backgroundColor: 'transparent',
                  },
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&:hover': {
                  backgroundColor: statusConfig.backgroundColor,
                },
                '& .MuiSelect-icon': {
                  color: statusConfig.color,
                },
              }}
            >
              <MenuItem value={ReviewStatus.PENDING}>Pending</MenuItem>
              <MenuItem value={ReviewStatus.IN_PROGRESS}>In Progress</MenuItem>
              <MenuItem value={ReviewStatus.APPROVED}>Approved</MenuItem>
              <MenuItem value={ReviewStatus.REJECTED}>Rejected</MenuItem>
            </Select>

            {/* Save/Feedback Actions - Always Right Aligned */}
            {(onSave || onCancel || onFeedback) && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {onFeedback && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FeedbackIcon />}
                    onClick={onFeedback}
                    sx={{ height: 32, fontSize: '0.8125rem' }}
                  >
                    Feedback
                  </Button>
                )}
                {onSave && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={onSave}
                    disabled={!isDirty || isSaving}
                    sx={{ height: 32, fontSize: '0.8125rem' }}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                )}
                {onCancel && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={onCancel}
                    sx={{ height: 32, fontSize: '0.8125rem' }}
                  >
                    Cancel
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Metadata row */}
      <Box sx={{ 
        px: 3, 
        py: 2,
        display: 'flex', 
        alignItems: 'center', 
        gap: 3,
        flexWrap: 'wrap',
        backgroundColor: 'background.paper'
      }}>
        {/* Target Standards */}
        {prediction.target_cc_standard_codes && prediction.target_cc_standard_codes.length > 0 && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Flag fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Target:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {prediction.target_cc_standard_codes.slice(0, 4).map((code, idx) => (
                  <Chip 
                    key={idx} 
                    label={code} 
                    size="small" 
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.75rem', fontWeight: 500 }}
                  />
                ))}
                {prediction.target_cc_standard_codes.length > 4 && (
                  <Tooltip
                    title={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Additional Target Standards:
                        </Typography>
                        {prediction.target_cc_standard_codes.slice(4).map((code, idx) => (
                          <Typography key={idx} variant="body2" component="div">
                            • {code}
                          </Typography>
                        ))}
                      </Box>
                    }
                    placement="bottom-start"
                    arrow
                  >
                    <Chip
                      label={`+${prediction.target_cc_standard_codes.length - 4} more`}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.75rem',
                        cursor: 'help',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        }
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ height: 24, alignSelf: 'center' }} />
          </>
        )}

        {/* Target Standard IDs */}
        {prediction.target_standard_ids && prediction.target_standard_ids.length > 0 && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Flag fontSize="small" sx={{ color: 'secondary.main' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Target IDs:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {prediction.target_standard_ids.slice(0, 3).map((id, idx) => (
                  <Tooltip key={idx} title={id} arrow>
                    <Chip
                      label={id.substring(0, 8) + '...'}
                      size="small"
                      color="secondary"
                      variant="outlined"
                      sx={{ height: 22, fontSize: '0.75rem', fontWeight: 500, cursor: 'help' }}
                    />
                  </Tooltip>
                ))}
                {prediction.target_standard_ids.length > 3 && (
                  <Tooltip
                    title={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Additional Target Standard IDs:
                        </Typography>
                        {prediction.target_standard_ids.slice(3).map((id, idx) => (
                          <Typography key={idx} variant="body2" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            • {id}
                          </Typography>
                        ))}
                      </Box>
                    }
                    placement="bottom-start"
                    arrow
                  >
                    <Chip
                      label={`+${prediction.target_standard_ids.length - 3} more`}
                      size="small"
                      color="secondary"
                      sx={{
                        height: 22,
                        fontSize: '0.75rem',
                        cursor: 'help',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        }
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ height: 24, alignSelf: 'center' }} />
          </>
        )}

        {/* Assignee */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Assignee:
          </Typography>
          <Select
            value={localAssignee}
            onChange={handleAssigneeChange}
            size="small"
            disabled={updateAssigneeMutation.isPending}
            displayEmpty
            sx={{
              minWidth: 140,
              height: 28,
              fontSize: '0.75rem',
              '& .MuiSelect-select': {
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
              },
            }}
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.75rem' }}>
                    Unassigned
                  </Typography>
                );
              }
              return (
                <Chip
                  label={selected.split('@')[0]}
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.75rem', fontWeight: 500 }}
                />
              );
            }}
          >
            <MenuItem value="">
              <em>Unassigned</em>
            </MenuItem>
            {availableAssignees.map((assignee) => (
              <MenuItem key={assignee} value={assignee}>
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{assignee}</Typography>
              </MenuItem>
            ))}
          </Select>
          {updateAssigneeMutation.isPending && (
            <CircularProgress size={14} />
          )}
        </Box>

        {/* Reviewed By */}
        {prediction.reviewed_by && prediction.reviewed_by.length > 0 && (
          <>
            <Divider orientation="vertical" flexItem sx={{ height: 24, alignSelf: 'center' }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <School fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Reviewed:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {prediction.reviewed_by.slice(0, 3).map((email, idx) => (
                  <Chip 
                    key={idx} 
                    label={email.split('@')[0]} 
                    size="small" 
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.75rem' }}
                  />
                ))}
                {prediction.reviewed_by.length > 3 && (
                  <Chip 
                    label={`+${prediction.reviewed_by.length - 3}`} 
                    size="small" 
                    sx={{ height: 22, fontSize: '0.75rem' }}
                  />
                )}
              </Box>
            </Box>
          </>
        )}

        {/* Divider before tag actions */}
        {(prediction.reviewed_by && prediction.reviewed_by.length > 0) && (onViewTags || onGenerateTags) && (
          <Divider orientation="vertical" flexItem sx={{ height: 24, alignSelf: 'center' }} />
        )}

        {/* Tag Actions */}
        {(onViewTags || onGenerateTags) && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {onViewTags && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<ViewIcon />}
                onClick={onViewTags}
                disabled={!prediction.spine_tag_ids?.length}
                sx={{ height: 28, fontSize: '0.75rem', px: 1.5 }}
              >
                View Tags
                {prediction.spine_tag_ids?.length ? (
                  <Chip
                    label={prediction.spine_tag_ids.length}
                    size="small"
                    sx={{ height: 14, fontSize: '0.65rem', ml: 0.5, minWidth: 16 }}
                  />
                ) : null}
              </Button>
            )}
            {onGenerateTags && (
              <Tooltip
                title={
                  prediction.review_status !== ReviewStatus.APPROVED
                    ? `Cannot generate tags. Prediction must be APPROVED (currently ${prediction.review_status})`
                    : 'Generate SPINE tags'
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<GenerateIcon />}
                    onClick={onGenerateTags}
                    disabled={isGeneratingTags || prediction.review_status !== ReviewStatus.APPROVED}
                    sx={{ height: 28, fontSize: '0.75rem', px: 1.5 }}
                  >
                    {isGeneratingTags ? 'Generating...' : 'Generate Tags'}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        )}

        {/* Spacer to push technical details to the right */}
        <Box sx={{ flex: 1 }} />

        {/* Technical details - smaller, less prominent */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
            {prediction.product || 'N/A'} · {prediction.experiment_version || 'N/A'}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem', fontFamily: 'monospace' }}>
            {prediction.id}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}
